import { useAuth0 } from '@auth0/auth0-react';
import type { Address, Party } from '@my/directory-api';
import { useLocations, useParties } from '@my/directory-api/hooks';
import { tmsApi } from '@my/tms-api';
import { orderTaskKeys, useOrders } from '@my/tms-api/hooks';
import type { OrderTask } from '@my/tms-api';
import { ErrorBanner, PageHeader } from '@my/ui';
import { useQueries } from '@tanstack/react-query';
import { CalendarClock, ClipboardCheck, Clock, PackageCheck, PenLine, Truck } from 'lucide-react';
import { useMemo } from 'react';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { fmtTodayLong, todayISO } from '@/utils/dates';
import {
  buildDashboardOrders,
  computeDashboardMetrics,
  earliestTodayTime,
  hasTaskToday,
  isUnscheduled,
} from './lib/dashboardData';
import { KpiCard } from './components/KpiCard';
import { NeedsAttentionPanel } from './components/NeedsAttentionPanel';
import { OrderPipelineBar } from './components/OrderPipelineBar';
import { QuickActions } from './components/QuickActions';
import { RecentOrdersPanel } from './components/RecentOrdersPanel';
import { TodaysActivityPanel } from './components/TodaysActivityPanel';

function getGivenName(user: ReturnType<typeof useAuth0>['user']): string {
  if (!user) return 'there';
  const given = user.given_name?.trim();
  if (given) return given;
  return user.nickname ?? user.email ?? 'there';
}

/**
 * TMS Dashboard. Composes:
 *   - PageHeader with greeting
 *   - "Today" KPI strip
 *   - All-time order pipeline bar
 *   - Three panels: Today's Activity, Recent Orders, Needs Attention
 *   - Quick action buttons
 *
 * Wired to the live TMS API: orders come from `useOrders(orgId)`, customer
 * names from the Directory parties book, and each order's tasks are fanned
 * out via `useQueries` + `getOrderTasks` (sharing the Order Details cache
 * key). `buildDashboardOrders` resolves all of that into the flat read-model
 * the panels consume — see `lib/dashboardData.ts`.
 */
export function DashboardPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const todayIso = todayISO();

  // Live lookups that hydrate the dashboard.
  const ordersQuery = useOrders(orgId);
  const customersQuery = useParties(orgId, 'Customer');
  const locationsQuery = useLocations(orgId);

  const orders = ordersQuery.data ?? [];

  // One tasks query per order — same key + queryFn as the Order Details page,
  // so the cache is shared. The orders-list endpoint doesn't embed tasks.
  const taskQueries = useQueries({
    queries: orders.map((o) => ({
      queryKey: orderTaskKeys.byOrder(o.id),
      queryFn: () => tmsApi.getOrderTasks(o.id),
      enabled: !!orgId,
      staleTime: 30 * 1000,
    })),
  });

  const customersById = useMemo(() => {
    const map = new Map<string, Party>();
    for (const p of customersQuery.data ?? []) map.set(p.id, p);
    return map;
  }, [customersQuery.data]);

  const addressById = useMemo(() => {
    const map = new Map<string, Address>();
    for (const a of locationsQuery.data ?? []) map.set(a.id, a);
    return map;
  }, [locationsQuery.data]);

  // Resolve the live orders + their tasks into the panels' read-model.
  const dashboardOrders = useMemo(() => {
    const list = ordersQuery.data ?? [];
    const tasksByOrderId = new Map<string, OrderTask[]>();
    list.forEach((o, i) => tasksByOrderId.set(o.id, taskQueries[i]?.data ?? []));
    return buildDashboardOrders(list, tasksByOrderId, customersById, addressById);
  }, [ordersQuery.data, taskQueries, customersById, addressById]);

  const metrics = useMemo(
    () => computeDashboardMetrics(dashboardOrders, todayIso),
    [dashboardOrders, todayIso],
  );

  const todaysOrders = useMemo(
    () =>
      dashboardOrders
        .filter((o) => hasTaskToday(o, todayIso))
        .sort((a, b) =>
          earliestTodayTime(a, todayIso).localeCompare(earliestTodayTime(b, todayIso)),
        ),
    [dashboardOrders, todayIso],
  );

  const recentOrders = useMemo(
    () => [...dashboardOrders].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 7),
    [dashboardOrders],
  );

  const attentionOrders = useMemo(
    () => dashboardOrders.filter(isUnscheduled),
    [dashboardOrders],
  );

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title="Dashboard"
        description={`Welcome back ${getGivenName(user)}! Here's what's happening today.`}
      />

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title="No organisation linked to this account"
            message="Your access token does not include a TMS organisation claim. Contact an administrator."
          />
        )}

        {orgId && ordersQuery.isError && (
          <ErrorBanner title="Failed to load orders" message={ordersQuery.error.message} />
        )}

        {/* ── KPI row ── */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Today
            </span>
            <span className="text-xs text-slate-400">{fmtTodayLong()}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <KpiCard
              icon={Clock}
              label="Total Jobs"
              value={metrics.todayTotal}
              iconBg="bg-slate-100"
              iconColor="text-slate-500"
            />
            <KpiCard
              icon={PenLine}
              label="Pending"
              value={metrics.pending}
              iconBg="bg-blue-100"
              iconColor="text-blue-500"
            />
            <KpiCard
              icon={Truck}
              label="In Progress"
              value={metrics.inProgress}
              iconBg="bg-orange-100"
              iconColor="text-orange-600"
            />
            <KpiCard
              icon={PackageCheck}
              label="Completed"
              value={metrics.completed}
              iconBg="bg-green-100"
              iconColor="text-green-600"
            />
            <KpiCard
              icon={CalendarClock}
              label="Unscheduled"
              value={metrics.unscheduled}
              iconBg="bg-slate-100"
              iconColor="text-slate-400"
              alert
            />
            <KpiCard
              icon={ClipboardCheck}
              label="Missing POD"
              value={metrics.missingPod}
              iconBg="bg-slate-100"
              iconColor="text-slate-400"
              danger
            />
          </div>
          {orgId && ordersQuery.isPending && (
            <p className="text-center text-xs text-slate-400">Loading…</p>
          )}
        </div>

        {/* ── Status distribution bar ── */}
        <OrderPipelineBar counts={metrics.countsByStatus} total={metrics.total} />

        {/* ── Three panels ── */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <TodaysActivityPanel orders={todaysOrders} todayIso={todayIso} />
          <RecentOrdersPanel orders={recentOrders} />
          <NeedsAttentionPanel orders={attentionOrders} />
        </div>

        {/* ── Quick actions ── */}
        <QuickActions />
      </main>
    </div>
  );
}
